/**
 * Journal Lock Store - privacy gate for the Journal module.
 *
 * The Journal is hidden from the main navigation and locked behind a PIN.
 * It becomes reachable only via a keyboard shortcut, which "reveals" the
 * module and shows a lock screen. The PIN is stored only as a salted
 * SHA-256 hash in local IndexedDB (appSettings) — the raw PIN is never
 * persisted.
 *
 * State machine:
 * - revealed=false            → Journal is completely hidden.
 * - revealed=true, locked=true → lock screen shown (set PIN or enter PIN).
 * - revealed=true, locked=false → Journal module is open.
 *
 * The journal auto-locks again (revealed=false, locked=true) on hide().
 */

import { create } from 'zustand';
import { db } from '@/services/localDb';

// ─── Settings keys ───────────────────────────────────────────────────────────

const PIN_HASH_KEY = 'journal.pinHash';
const PIN_SALT_KEY = 'journal.pinSalt';

// ─── Hashing helpers (Web Crypto, with a safe fallback) ──────────────────────

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function generateSalt(): string {
  const arr = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return toHex(arr.buffer);
}

/** Hash a PIN with its salt using SHA-256 (fallback to a simple hash if crypto.subtle is unavailable). */
async function hashPin(pin: string, salt: string): Promise<string> {
  const data = new TextEncoder().encode(`${salt}:${pin}`);
  if (globalThis.crypto?.subtle?.digest) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
    return toHex(digest);
  }
  // Deterministic fallback (non-crypto) — only used in environments without SubtleCrypto.
  let h = 0;
  const str = `${salt}:${pin}`;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return `fallback_${(h >>> 0).toString(16)}`;
}

async function readSetting(key: string): Promise<string | null> {
  try {
    const row = await db.appSettings.get(key);
    return typeof row?.value === 'string' ? row.value : null;
  } catch {
    return null;
  }
}

async function writeSetting(key: string, value: string): Promise<void> {
  try {
    await db.appSettings.put({ key, value });
  } catch {
    // IndexedDB unavailable — PIN won't persist across reloads but UI still works.
  }
}

// ─── Store ───────────────────────────────────────────────────────────────────

export interface JournalLockState {
  /** Whether the Journal is currently revealed (shortcut pressed). */
  revealed: boolean;
  /** Whether the Journal is locked (PIN required to view). */
  locked: boolean;
  /** Whether a PIN has been configured yet. */
  hasPin: boolean;
  /** Whether the store has loaded persisted lock settings. */
  initialized: boolean;
  /** Last verification error (e.g. wrong PIN). */
  error: string | null;
}

export interface JournalLockActions {
  /** Load persisted PIN state from IndexedDB. */
  initialize: () => Promise<void>;
  /** Reveal the Journal (triggered by the keyboard shortcut). Stays locked. */
  reveal: () => void;
  /** Hide and re-lock the Journal. */
  hide: () => void;
  /** Set the PIN for the first time (or when none exists). */
  setPin: (pin: string) => Promise<void>;
  /** Attempt to unlock with a PIN. Returns true on success. */
  unlock: (pin: string) => Promise<boolean>;
  /** Re-lock without hiding (keeps the lock screen up). */
  lock: () => void;
  /** Clear the error state. */
  clearError: () => void;
}

export type JournalLockStore = JournalLockState & JournalLockActions;

export const useJournalLockStore = create<JournalLockStore>((set) => ({
  revealed: false,
  locked: true,
  hasPin: false,
  initialized: false,
  error: null,

  initialize: async () => {
    const hash = await readSetting(PIN_HASH_KEY);
    set({ hasPin: hash !== null, initialized: true });
  },

  reveal: () => {
    set({ revealed: true, locked: true, error: null });
  },

  hide: () => {
    set({ revealed: false, locked: true, error: null });
  },

  lock: () => {
    set({ locked: true, error: null });
  },

  setPin: async (pin) => {
    const trimmed = pin.trim();
    if (trimmed.length < 4) {
      set({ error: 'PIN must be at least 4 digits.' });
      return;
    }
    const salt = generateSalt();
    const hash = await hashPin(trimmed, salt);
    await writeSetting(PIN_SALT_KEY, salt);
    await writeSetting(PIN_HASH_KEY, hash);
    // Setting a PIN also unlocks the journal for this session.
    set({ hasPin: true, locked: false, revealed: true, error: null });
  },

  unlock: async (pin) => {
    const salt = await readSetting(PIN_SALT_KEY);
    const storedHash = await readSetting(PIN_HASH_KEY);
    if (!salt || !storedHash) {
      set({ error: 'No PIN set.' });
      return false;
    }
    const candidate = await hashPin(pin.trim(), salt);
    if (candidate === storedHash) {
      set({ locked: false, error: null });
      return true;
    }
    set({ error: 'Incorrect PIN.' });
    return false;
  },

  clearError: () => set({ error: null }),
}));
